import { ComponentType, useCallback, useEffect, useRef, useState } from 'react'
import { Box, Icon, Stack } from '@mui/material'
import {
    useTheme,
    IconButton,
    LeafIcon
} from '@thriveglobal/thrive-web-leafkit'
import debounce from 'lodash.debounce'

type Props<T> = {
    Component: ComponentType<T>
    componentsProps: Array<T & { key: string }>
    reducedPadding?: boolean
    hideNavButtons?: boolean
}

export default function HorizontalScroll<T>({
    Component,
    componentsProps,
    reducedPadding,
    hideNavButtons
}: Props<T>) {
    const {
        viewRef,
        gridRef,
        canScrollBackward,
        scrollBackward,
        canScrollForward,
        scrollForward
    } = useHorizontalScroll<T>({ componentsProps, hideNavButtons })

    const theme = useTheme()

    return (
        <Box sx={{ position: 'relative' }}>
            {canScrollBackward && (
                <NavButton direction="left" onClick={scrollBackward} />
            )}
            <Box ref={viewRef} sx={{ position: 'relative' }}>
                <Stack
                    data-testid="horizontal-scroll-grid"
                    ref={gridRef}
                    direction="row"
                    component={'ul'}
                    spacing={0}
                    sx={{
                        p: 0,
                        m: 0,
                        listStyleType: 'none',
                        overflowX: 'scroll',
                        scrollSnapType: 'x mandatory',
                        scrollbarWidth: 'none' /* Hide scrollbar for Firefox */,
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        } /* Hide scrollbar for Chrome, Safari and Opera */,
                        pb: '10px'
                    }}
                >
                    {componentsProps.map((componentProp) => {
                        return (
                            <Box
                                key={componentProp.key}
                                component={'li'}
                                sx={{
                                    display: 'flex',
                                    scrollSnapAlign: 'start',

                                    '&:not(:first-child)': {
                                        pl: theme.spacing(2)
                                    },
                                    [theme.breakpoints.up('md')]: {
                                        '&:not(:first-child)': {
                                            pl: reducedPadding
                                                ? theme.spacing(2)
                                                : theme.spacing(4)
                                        }
                                    }
                                }}
                            >
                                <Component {...componentProp} />
                            </Box>
                        )
                    })}
                </Stack>
            </Box>
            {canScrollForward && (
                <NavButton direction="right" onClick={scrollForward} />
            )}
        </Box>
    )
}

function NavButton({
    direction,
    onClick
}: {
    direction: 'left' | 'right'
    onClick?: () => void
}) {
    const theme = useTheme()

    const isRightNav = direction === 'right'

    return (
        <IconButton
            aria-label={`scroll ${direction}`}
            onClick={onClick}
            disableElevation={false}
            sx={{
                zIndex: 1,
                position: 'absolute',
                right: isRightNav ? '-24px' : undefined,
                left: !isRightNav ? '-24px' : undefined,
                top: 'calc(50% - 24px)',
                height: 48,
                width: 48,
                borderRadius: '50%',
                [theme.breakpoints.down('sm')]: {
                    right: isRightNav ? '-10px' : undefined,
                    left: !isRightNav ? '-10px' : undefined,
                    top: 'calc(50% - 15px)',
                    height: 30,
                    width: 30
                }
            }}
            variant={'contained'}
            color={'info'}
        >
            <LeafIcon icon={isRightNav ? 'arrow-right' : 'arrow-left'} />
        </IconButton>
    )
}

function useHorizontalScroll<T>({
    componentsProps,
    hideNavButtons
}: {
    hideNavButtons?: boolean
    componentsProps: Array<T & { key: string }>
}) {
    const gridRef = useRef<HTMLDivElement>(null)
    const viewRef = useRef<HTMLDivElement>(null)

    const [canScrollForward, setCanScrollForward] = useState(false)
    const [canScrollBackward, setCanScrollBackward] = useState(false)

    const [firstVisibleItem, setFirstVisibleItem] = useState<{
        el: HTMLDivElement
        isIntersecting: boolean
        hiddenWidth: number
    } | null>(null)
    const [lastVisibleItem, setLastVisibleItem] = useState<{
        el: HTMLDivElement
        isIntersecting: boolean
        hiddenWidth: number
    } | null>(null)

    const update = useCallback(() => {
        if (!gridRef.current) return

        const viewWidth = gridRef.current.clientWidth

        const { x: xOrigin, right: rightOrigin } =
            gridRef.current.getBoundingClientRect()

        const items = Array.from(
            gridRef.current?.children || []
        ) as HTMLDivElement[]

        const itemsWidthSum = items.reduce(
            (total, item) => total + item.clientWidth,
            0
        )

        if (items.length === 0) {
            setCanScrollForward(false)
            setCanScrollBackward(false)
            setFirstVisibleItem(null)
            setLastVisibleItem(null)
            return
        }

        if (itemsWidthSum <= viewWidth) {
            setCanScrollForward(false)
            setCanScrollBackward(false)
            setFirstVisibleItem({
                el: items[0],
                isIntersecting: false,
                hiddenWidth: 0
            })
            setLastVisibleItem({
                el: items[items.length - 1],
                isIntersecting: false,
                hiddenWidth: 0
            })
            return
        }

        items.forEach((item) => {
            const { right, left } = item.getBoundingClientRect()
            const _left = Math.round(left)
            const _right = Math.round(right)
            const _rightOrigin = Math.round(rightOrigin)
            const _xOrigin = Math.round(xOrigin)

            const isFirstVisibleItem = _right >= _xOrigin && _left <= _xOrigin

            const isLastVisibleItem =
                _left <= _rightOrigin && _right >= _rightOrigin

            if (isFirstVisibleItem) {
                const isIntersecting = _left < _xOrigin
                const hiddenWidth = _xOrigin - _left
                setFirstVisibleItem({ el: item, isIntersecting, hiddenWidth })
                setCanScrollBackward(
                    Boolean(isIntersecting || item.previousElementSibling)
                )
            }

            if (isLastVisibleItem) {
                const isIntersecting = _right > _rightOrigin
                const hiddenWidth = _right - _rightOrigin
                setLastVisibleItem({
                    el: item,
                    isIntersecting,
                    hiddenWidth
                })
                setCanScrollForward(
                    Boolean(isIntersecting || item.nextElementSibling)
                )
            }
        })
    }, [])

    const debouncedUpdateRef = useRef(debounce(update, 100))

    const scrollTo = useCallback((distance: any) => {
        gridRef.current?.scrollTo({
            left: distance,
            behavior: 'smooth'
        })
    }, [])

    const scrollForward = useCallback(() => {
        if (lastVisibleItem) {
            const { el, isIntersecting, hiddenWidth } = lastVisibleItem
            const nextEl = (el.nextElementSibling as HTMLDivElement) || null
            const onlyOneVisibleItem =
                lastVisibleItem.el === firstVisibleItem?.el

            if (onlyOneVisibleItem) {
                if (nextEl) {
                    scrollTo(nextEl.offsetLeft)

                    return
                }

                if (isIntersecting) {
                    scrollTo(el.offsetLeft + hiddenWidth - 3)
                }

                return
            }

            if (isIntersecting) {
                scrollTo(el.offsetLeft)
            } else {
                if (nextEl) {
                    scrollTo(nextEl.offsetLeft)
                } else {
                    scrollTo(gridRef.current?.scrollWidth)
                }
            }
        }
    }, [lastVisibleItem, firstVisibleItem])

    const scrollBackward = useCallback(() => {
        if (firstVisibleItem) {
            const { el, isIntersecting } = firstVisibleItem
            const viewWidth = gridRef.current?.clientWidth || 0

            if (isIntersecting) {
                scrollTo(el.offsetLeft - viewWidth + el.clientWidth)
            } else {
                scrollTo(el.offsetLeft - viewWidth)
            }
        }
    }, [firstVisibleItem])

    useEffect(() => {
        update()
    }, [componentsProps, update])

    useEffect(() => {
        const current = gridRef.current
        current?.addEventListener('scroll', debouncedUpdateRef.current)

        const debouncedUpdate = debouncedUpdateRef.current

        return () => current?.removeEventListener('scroll', debouncedUpdate)
    }, [update])

    useEffect(() => {
        const resizeObserver = new ResizeObserver(debouncedUpdateRef.current)
        resizeObserver.observe(gridRef?.current as HTMLDivElement)

        return () => resizeObserver.disconnect()
    }, [update])

    return {
        gridRef,
        viewRef,
        canScrollForward: !hideNavButtons && canScrollForward,
        canScrollBackward: !hideNavButtons && canScrollBackward,
        scrollForward,
        scrollBackward
    }
}
